/**
 * MediaInsightSkeleton — tailored loading state for the media insight page.
 *
 * Mirrors the live layout so the skeleton → loaded transition feels continuous:
 * - Top header row (back button, breadcrumbs, state badge, chat + action buttons)
 * - Transcript toolbar row (search, people, copy, edit)
 * - 4 speaker paragraph blocks (avatar + name + 3 text lines) — matches the
 *   in-view transcript skeleton in media-insight-view.tsx for continuity
 * - Bottom transport bar (player controls)
 *
 * No client hooks used — safe to render from any isLoading branch.
 */
import { Skeleton } from "@speakai/ui/skeleton";

export function MediaInsightSkeleton() {
  return (
    <div
      className="flex flex-col h-[calc(100dvh-var(--topbar-height)-var(--launch-bar-height))]"
      data-testid="media-insight-skeleton"
      aria-busy="true"
      aria-label="Loading media insight"
    >
      {/* Header row — matches MediaInsightHeader layout */}
      <div className="flex items-center gap-2 sm:gap-3 px-3 sm:px-4 lg:px-6 py-2.5 sm:py-3 border-b border-border bg-card shrink-0">
        {/* Back button */}
        <Skeleton className="h-8 w-8 rounded-md shrink-0" />
        {/* Breadcrumb pieces */}
        <div className="flex-1 min-w-0 flex items-center gap-2">
          <Skeleton className="hidden sm:block h-3.5 w-16 rounded" />
          <Skeleton className="hidden sm:block h-3 w-3 rounded-full" />
          <Skeleton className="hidden sm:block h-3.5 w-24 rounded" />
          <Skeleton className="hidden sm:block h-3 w-3 rounded-full" />
          <Skeleton className="h-4 w-40 sm:w-56 rounded" />
          <Skeleton className="hidden sm:block h-4 w-16 rounded-full" />
        </div>
        {/* Right-side actions: chat button + edit/export */}
        <Skeleton className="h-8 w-20 rounded-md" />
        <Skeleton className="h-8 w-8 rounded-md" />
        <Skeleton className="h-8 w-8 rounded-md" />
      </div>

      {/* Transcript toolbar row (search, icons, edit) */}
      <div className="flex items-center gap-2 px-3 sm:px-4 lg:px-6 py-2 border-b border-border bg-card/50 shrink-0">
        <Skeleton className="h-8 w-48 sm:w-64 rounded-md" />
        <Skeleton className="h-8 w-8 rounded-md" />
        <Skeleton className="h-8 w-8 rounded-md" />
        <div className="flex-1" />
        <Skeleton className="hidden sm:block h-8 w-24 rounded-md" />
      </div>

      {/* Transcript paragraph skeletons — matches the in-view loading pattern */}
      <div className="flex-1 min-h-0 overflow-hidden">
        <div className="p-4 sm:p-6 space-y-6">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="space-y-2">
              <div className="flex items-center gap-2">
                <Skeleton className="h-6 w-6 rounded-full" />
                <Skeleton className="h-3.5 w-32 rounded" />
                <Skeleton className="h-3 w-10 rounded" />
              </div>
              <Skeleton className="h-4 w-full rounded" />
              <Skeleton className="h-4 w-[92%] rounded" />
              <Skeleton className="h-4 w-[85%] rounded" />
            </div>
          ))}
        </div>
      </div>

      {/* Bottom transport bar (sticky player) */}
      <div className="shrink-0 border-t border-border bg-card px-3 sm:px-4 lg:px-6 py-2.5">
        <div className="flex items-center gap-3">
          <Skeleton className="h-9 w-9 rounded-full shrink-0" />
          <Skeleton className="h-8 w-8 rounded-md shrink-0" />
          <Skeleton className="h-8 w-8 rounded-md shrink-0" />
          <div className="flex-1 flex items-center gap-2 min-w-0">
            <Skeleton className="h-3 w-10 rounded" />
            <Skeleton className="h-1.5 flex-1 rounded-full" />
            <Skeleton className="h-3 w-10 rounded" />
          </div>
          <Skeleton className="hidden sm:block h-8 w-8 rounded-md shrink-0" />
          <Skeleton className="hidden sm:block h-8 w-8 rounded-md shrink-0" />
        </div>
      </div>
    </div>
  );
}
